import React from 'react';
import { Globe, Shield } from 'lucide-react';
import { SSOProvider, Role } from '../../types';
import { Theme } from '../../ThemeContext';

interface SSOProviderEditModeProps {
  provider: SSOProvider;
  roles: Role[];
  theme: Theme;
  editName: string;
  editEnabled: boolean;
  editDefaultRole: string | undefined;
  editAutoCreateUsers: boolean;
  editOidcClientId: string;
  editOidcClientSecret: string;
  editOidcDiscoveryUrl: string;
  editOidcScopes: string;
  editSamlEntityId: string;
  editSamlSsoUrl: string;
  editSamlSloUrl: string;
  editSamlX509Cert: string;
  onNameChange: (value: string) => void;
  onEnabledChange: (value: boolean) => void;
  onDefaultRoleChange: (value: string | undefined) => void;
  onAutoCreateUsersChange: (value: boolean) => void;
  onOidcClientIdChange: (value: string) => void;
  onOidcClientSecretChange: (value: string) => void;
  onOidcDiscoveryUrlChange: (value: string) => void;
  onOidcScopesChange: (value: string) => void;
  onSamlEntityIdChange: (value: string) => void;
  onSamlSsoUrlChange: (value: string) => void;
  onSamlSloUrlChange: (value: string) => void;
  onSamlX509CertChange: (value: string) => void;
}

const SSOProviderEditMode: React.FC<SSOProviderEditModeProps> = ({
  provider,
  roles,
  theme,
  editName,
  editEnabled,
  editDefaultRole,
  editAutoCreateUsers,
  editOidcClientId,
  editOidcClientSecret,
  editOidcDiscoveryUrl,
  editOidcScopes,
  editSamlEntityId,
  editSamlSsoUrl,
  editSamlSloUrl,
  editSamlX509Cert,
  onNameChange,
  onEnabledChange,
  onDefaultRoleChange,
  onAutoCreateUsersChange,
  onOidcClientIdChange,
  onOidcClientSecretChange,
  onOidcDiscoveryUrlChange,
  onOidcScopesChange,
  onSamlEntityIdChange,
  onSamlSsoUrlChange,
  onSamlSloUrlChange,
  onSamlX509CertChange,
}) => {
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: 12,
    background: theme.background,
    border: `1px solid ${theme.border}`,
    borderRadius: 4,
    color: theme.text,
    fontSize: 14,
    fontFamily: 'inherit',
  };

  const labelStyle: React.CSSProperties = {
    color: theme.textSecondary,
    fontSize: 14,
    fontWeight: 600,
    display: 'block',
    marginBottom: 8,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Basic Information */}
      <div style={{
        background: theme.cardBackground,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        padding: 24,
      }}>
        <h2 style={{ color: theme.text, fontSize: 18, marginBottom: 16, marginTop: 0 }}>
          Basic Information
        </h2>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>
              Provider Name
            </label>
            <input
              type="text"
              value={editName}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Enter provider name..."
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>
              Provider Type
            </label>
            <div style={{ 
              color: theme.text, 
              fontSize: 14,
              padding: 12,
              background: theme.background,
              border: `1px solid ${theme.border}`,
              borderRadius: 4,
            }}>
              <span style={{
                padding: '4px 8px',
                background: provider.provider_type === 'oidc' ? '#e0f2fe' : '#f0e6ff',
                color: provider.provider_type === 'oidc' ? '#0369a1' : '#7c3aed',
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 600,
                textTransform: 'uppercase',
              }}>
                {provider.provider_type}
              </span>
            </div>
            <div style={{ color: theme.textSecondary, fontSize: 12, marginTop: 4 }}>
              Provider type cannot be changed
            </div>
          </div>

          <div>
            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={editEnabled}
                onChange={(e) => onEnabledChange(e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              Enabled
            </label>
            <div style={{ color: theme.textSecondary, fontSize: 12, marginTop: 4 }}>
              Enable this provider for SSO login
            </div>
          </div>

          <div>
            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={editAutoCreateUsers}
                onChange={(e) => onAutoCreateUsersChange(e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              Auto-Create Users
            </label>
            <div style={{ color: theme.textSecondary, fontSize: 12, marginTop: 4 }}>
              Automatically create users on first SSO login
            </div>
          </div>

          <div>
            <label style={labelStyle}>
              Default Role
            </label>
            <select
              value={editDefaultRole || ''}
              onChange={(e) => onDefaultRoleChange(e.target.value || undefined)}
              style={inputStyle}
            >
              <option value="">No default role</option>
              {roles.map(role => (
                <option key={role.role_id} value={role.role_id}>
                  {role.name.toUpperCase()}
                </option>
              ))}
            </select>
            <div style={{ color: theme.textSecondary, fontSize: 12, marginTop: 4 }}>
              Default role assigned to auto-created users
            </div>
          </div>
        </div>
      </div>

      {/* OIDC Configuration */}
      {provider.provider_type === 'oidc' && (
        <div style={{
          background: theme.cardBackground,
          border: `1px solid ${theme.border}`,
          borderRadius: 8,
          padding: 24,
        }}>
          <h2 style={{ color: theme.text, fontSize: 18, marginBottom: 16, marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Globe size={20} />
            OIDC Configuration
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle}>
                Client ID
              </label>
              <input
                type="text"
                value={editOidcClientId}
                onChange={(e) => onOidcClientIdChange(e.target.value)}
                placeholder="Enter OIDC client ID..."
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
            </div>

            <div>
              <label style={labelStyle}>
                Client Secret
              </label>
              <input
                type="password"
                value={editOidcClientSecret}
                onChange={(e) => onOidcClientSecretChange(e.target.value)}
                placeholder="Enter OIDC client secret..."
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
            </div>

            <div>
              <label style={labelStyle}>
                Discovery URL
              </label>
              <input
                type="url"
                value={editOidcDiscoveryUrl}
                onChange={(e) => onOidcDiscoveryUrlChange(e.target.value)}
                placeholder="https://provider.com/.well-known/openid-configuration"
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
              <div style={{ color: theme.textSecondary, fontSize: 12, marginTop: 4 }}>
                OIDC discovery endpoint URL
              </div>
            </div>

            <div>
              <label style={labelStyle}>
                Scopes
              </label>
              <input
                type="text"
                value={editOidcScopes}
                onChange={(e) => onOidcScopesChange(e.target.value)}
                placeholder="openid, email, profile"
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
              <div style={{ color: theme.textSecondary, fontSize: 12, marginTop: 4 }}>
                Comma-separated list of OAuth scopes
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SAML Configuration */}
      {provider.provider_type === 'saml' && (
        <div style={{
          background: theme.cardBackground,
          border: `1px solid ${theme.border}`,
          borderRadius: 8,
          padding: 24,
        }}>
          <h2 style={{ color: theme.text, fontSize: 18, marginBottom: 16, marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Shield size={20} />
            SAML Configuration
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle}>
                Entity ID
              </label>
              <input
                type="text"
                value={editSamlEntityId}
                onChange={(e) => onSamlEntityIdChange(e.target.value)}
                placeholder="https://provider.com"
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
              <div style={{ color: theme.textSecondary, fontSize: 12, marginTop: 4 }}>
                SAML Identity Provider Entity ID
              </div>
            </div>

            <div>
              <label style={labelStyle}>
                SSO URL
              </label>
              <input
                type="url"
                value={editSamlSsoUrl}
                onChange={(e) => onSamlSsoUrlChange(e.target.value)}
                placeholder="https://provider.com/sso/saml"
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
              <div style={{ color: theme.textSecondary, fontSize: 12, marginTop: 4 }}>
                SAML Single Sign-On URL
              </div>
            </div>

            <div>
              <label style={labelStyle}>
                SLO URL (Optional)
              </label>
              <input
                type="url"
                value={editSamlSloUrl}
                onChange={(e) => onSamlSloUrlChange(e.target.value)}
                placeholder="https://provider.com/slo/saml"
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
              <div style={{ color: theme.textSecondary, fontSize: 12, marginTop: 4 }}>
                SAML Single Logout URL (optional)
              </div>
            </div>

            <div>
              <label style={labelStyle}>
                X.509 Certificate
              </label>
              <textarea
                value={editSamlX509Cert}
                onChange={(e) => onSamlX509CertChange(e.target.value)}
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                style={{
                  ...inputStyle,
                  fontFamily: 'monospace',
                  minHeight: 150,
                  resize: 'vertical',
                }}
              />
              <div style={{ color: theme.textSecondary, fontSize: 12, marginTop: 4 }}>
                X.509 certificate for SAML signature verification
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SSOProviderEditMode;
